import { Component, Inject, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ApiService } from '../../services/api.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect } from '@angular/material/select';
import * as XLSX from 'xlsx';
import { AccountUploadDialog } from '../account-upload/account-upload.component';
import { BilldetailsDialog } from '../billdetails/billdetails.component';
import { Customer } from '../../model/customer.model';
import { PhoneMatchResult, BillUpdateRequest, BillUpdateItem } from '../../model/phone-match.model';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-account-upload-view',
  templateUrl: './account-upload-view.component.html',
  styleUrls: ['./account-upload-view.component.scss']
})
export class AccountUploadViewDialog implements OnInit, AfterViewInit {
  loading = false;
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [
    'transferId',
    'transferDate',
    'description',
    'receivedAccount',
    'sentAmount',
    'receivedAmount',
    'balance',
    'phoneTo',
    'phoneFrom',
    'transactionType',
    'merchantName'
  ];
  totalRecords = 0;
  totalSentAmount = 0;
  totalReceivedAmount = 0;
  searchField: string = 'all';
  searchText: string = '';
  originalData: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    public dialogRef: MatDialogRef<AccountUploadViewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: number; cycleStart: string; cycleEnd: string },
    private apiService: ApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadUploadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  loadUploadData(): void {
    this.loading = true;
    this.apiService.getAccountUploadsPaginated(
      this.data.employeeId,
      this.data.cycleStart,
      this.data.cycleEnd,
      0,
      1000 // Load all records for client-side pagination
    ).subscribe({
      next: (response: any) => {
        this.originalData = response.data || [];
        this.dataSource.data = this.originalData;
        this.totalRecords = response.total || 0;
        this.calculateTotals();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading account upload data:', error);
        this.loading = false;
      }
    });
  }

  calculateTotals(): void {
    this.totalSentAmount = this.dataSource.data.reduce((sum: number, item: any) => sum + (parseFloat(item.sentAmount) || 0), 0);
    this.totalReceivedAmount = this.dataSource.data.reduce((sum: number, item: any) => sum + (parseFloat(item.receivedAmount) || 0), 0);
  }

  applySearch(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.dataSource.data = this.originalData;
      return;
    }

    const searchTerm = this.searchText.toLowerCase().trim();

    if (this.searchField === 'all') {
      this.dataSource.data = this.originalData.filter((item: any) => {
        return Object.values(item).some((value: any) =>
          value !== null && value !== undefined && value.toString().toLowerCase().includes(searchTerm)
        );
      });
    } else {
      this.dataSource.data = this.originalData.filter((item: any) => {
        const value = item[this.searchField];
        return value !== null && value !== undefined && value.toString().toLowerCase().includes(searchTerm);
      });
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchField = 'all';
    this.dataSource.data = this.originalData;
  }

  exportToExcel(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.dataSource.data);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Account Upload Data');
    XLSX.writeFile(wb, `account_upload_${this.data.employeeId}_${this.data.cycleStart}_${this.data.cycleEnd}.xlsx`);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  triggerPhoneMatching(): void {
    this.loading = true;
    
    // Call the backend to match phones from existing uploaded data
    this.apiService.matchPhonesFromExistingData(
      this.data.employeeId,
      this.data.cycleStart,
      this.data.cycleEnd
    ).subscribe({
      next: (response: PhoneMatchResult[]) => {
        this.loading = false;
        this.openVerificationDialog(response);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open('Error processing phone matching: ' + (error.message || 'Unknown error'), 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  openVerificationDialog(matchResults: PhoneMatchResult[]): void {
    const dialogRef = this.dialog.open(AccountUploadDialog, {
      width: '900px',
      data: {
        employeeId: this.data.employeeId,
        cycleStart: this.data.cycleStart,
        cycleEnd: this.data.cycleEnd,
        matchResults: matchResults,
        verificationOnly: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Bills updated successfully', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success']
        });
        this.loadUploadData(); // Refresh the data
      }
    });
  }

  openBillDetails(customerId: number, customerName: string): void {
    const customer: Customer = {
      customerId: customerId,
      customerName: customerName,
      customerPhone: 0,
      customerPhone2: null,
      customerPhone3: null,
      customerEmail: '',
      customerAddress: '',
      customerStatus: '',
      customerJoinedDate: '',
      customerUnitRate: 0,
      defaultUnitRate: 0,
      customerDueAmount: 0,
      customerAction: '',
      areaCode: 0,
      areaName: '',
      meterId: 0,
      previousReading: 0,
      currentReading: 0,
      latitude: 0,
      longitude: 0
    };

    const dialogRef = this.dialog.open(BilldetailsDialog, {
      width: '1200px',
      data: customer
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadUploadData(); // Refresh the data
      }
    });
  }
}
